import { CallsFilterFormValue } from '@features/Admin/Campaign/model/type';
import { ReportColumn } from '@features/Admin/Campaign/constants/reportColumns.constants';
import { CampaignCallStatus } from '@shared/constants/recipient-status.constants';
import { SentimentDto } from '@shared/services/apiService/apiService';

import { RecipientData, SortConfig } from '@pages/Admin/Campaign/ui/types';
import { getTotalSentimentsScore } from '@pages/Admin/Campaign/ui/utils';

export const applyFiltersToCalls = (
  allCalls: RecipientData[],
  filterParams: CallsFilterFormValue,
): RecipientData[] => {
  return allCalls.filter((call) => {
    const statusMatch = filterParams.statuses === 'all' || call.status === filterParams.statuses;
    const typeMatch = filterParams.types === 'all' || call.type === filterParams.types;
    const numberSearchMatch =
      !filterParams.numberSearch || call.phoneNumber?.includes(filterParams.numberSearch);
    const callIdMatch = !filterParams.callIdSearch || call.id?.includes(filterParams.callIdSearch);
    const endedReasonMatch =
      filterParams.endedReason === 'all' || call.endedReason === filterParams.endedReason;

    const callDate = call.createdAt ? new Date(call.createdAt).getTime() : 0;
    const startDate = filterParams.start ? new Date(filterParams.start).getTime() : 0;
    const endDate = filterParams.end ? new Date(filterParams.end).getTime() : Infinity;

    const dateMatch = callDate >= startDate && callDate <= endDate;

    return (
      statusMatch && typeMatch && numberSearchMatch && callIdMatch && endedReasonMatch && dateMatch
    );
  });
};

export type MissingEndedReasonKey = 'endedReasonMissing' | 'endedReasonNotEnded';

export const getMissingEndedReasonKey = ({
  status,
  interactionResult,
}: Pick<RecipientData, 'status' | 'interactionResult'>): MissingEndedReasonKey | null => {
  if (interactionResult) return null;
  return status === CampaignCallStatus.ENDED ? 'endedReasonMissing' : 'endedReasonNotEnded';
};

export const areCallsProcessing = (calls: RecipientData[]) => {
  const hasProcessingCalls = calls.some(
    (call) =>
      call.status === CampaignCallStatus['IN-PROGRESS'] ||
      call.status === CampaignCallStatus.RINGING,
  );
  const hasEndedCalls = calls.some((call) => call.status === CampaignCallStatus.ENDED);

  return !hasProcessingCalls && hasEndedCalls;
};

export const sortCalls = (calls: RecipientData[], sortConfig: SortConfig) => {
  if (!sortConfig.key) return calls;

  const sortedCalls = [...calls].sort((a, b) => {
    let aValue: number | string | SentimentDto[] = a[sortConfig.key!] ?? '';
    let bValue: number | string | SentimentDto[] = b[sortConfig.key!] ?? '';

    if (sortConfig.key === 'sentiments') {
      aValue = a.sentiments.length ? getTotalSentimentsScore(a.sentiments) : -Infinity;
      bValue = b.sentiments.length ? getTotalSentimentsScore(b.sentiments) : -Infinity;
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return sortedCalls;
};

export const transformToDDMMYYYY = (timestamp: string) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
    .format(date)
    .replace(/\//g, '.')
    .replace(',', '');
};

export const formatDatePart = (timestamp: string) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .format(date)
    .replace(/\//g, '.');
};

export const formatTimePart = (timestamp: string) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);
};

export const buildReportQueryParameters = (
  data: CallsFilterFormValue,
  selectedColumns: string[] = [],
  options?: { includeAdditionalColumns?: boolean; separatedAnalysisSummary?: boolean },
): string => {
  const params = new URLSearchParams();
  for (const key in data) {
    const value = data[key as keyof CallsFilterFormValue];
    if (value !== null && value !== undefined && value !== '' && value !== 'all') {
      params.append(key, value.toString());
    }
  }
  if (options?.includeAdditionalColumns) {
    params.append('additionalColumns', selectedColumns.join(','));
  } else if (selectedColumns.length) {
    params.append('additionalColumns', selectedColumns.join(','));
  }
  if (options?.separatedAnalysisSummary === true) {
    params.append('separatedAnalysisSummary', 'true');
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

export const getStoredAdditionalColumns = (storageKey: string): string[] | null => {
  try {
    const rawValue = localStorage.getItem(storageKey);
    if (rawValue === null) return null;
    const parsedValue = JSON.parse(rawValue);
    if (!Array.isArray(parsedValue)) return null;
    if (!parsedValue.every((item) => typeof item === 'string')) return null;
    return parsedValue;
  } catch {
    return null;
  }
};

export const saveSelectedAdditionalColumns = (storageKey: string, columns: string[]): void => {
  localStorage.setItem(storageKey, JSON.stringify(columns));
};

export const getInitialAdditionalColumns = (
  reportColumns: readonly ReportColumn[],
  storedColumns: string[] | null,
): string[] => {
  const optionalColumns = reportColumns
    .filter((column) => !column.required)
    .map((column) => column.key);
  if (storedColumns === null) {
    return reportColumns
      .filter((column) => !column.required && column.defaultSelected)
      .map((column) => column.key);
  }
  return storedColumns.filter((columnKey) => optionalColumns.includes(columnKey));
};

export const getToggledAdditionalColumns = (
  selectedColumns: string[],
  columnKey: string,
): string[] => {
  return selectedColumns.includes(columnKey)
    ? selectedColumns.filter((key) => key !== columnKey)
    : [...selectedColumns, columnKey];
};
